import { atom, useAtomValue } from 'jotai';

import { useDeepMemo } from '@crossnokaye/ux-common';

import type { ConditionExpression } from '@repo/sdk/models/components';

import { evaluateStates } from './reactive-condition-evaluator';
import type { UseActiveStateOptions } from './types';

/**
 * Reactively evaluates an array of conditional states and returns the
 * highest-priority match.
 *
 * Backed by a derived Jotai atom — all ref reads and external state reads
 * (`eaiControlledDeviceIdsAtom`) go through `get: Getter`, so Jotai
 * automatically tracks dependencies and re-evaluates when any underlying
 * value changes.
 *
 * Drop-in replacement for the imperative `getActiveState`.
 *
 * @example
 * ```tsx
 * const activeState = useActiveState({ states: deviceState.states });
 * const variant = activeState?.variant ?? 'default';
 * ```
 */
export const useActiveState = <
  TState extends {
    conditionExpression?: ConditionExpression;
    isDefault?: boolean;
    priority?: number;
  },
>({
  states,
}: UseActiveStateOptions<TState>): TState | undefined => {
  const derivedAtom = useDeepMemo(() => atom((get) => evaluateStates(get, states)), [states]);
  return useAtomValue(derivedAtom);
};
